import { useState, useEffect } from "react";
import { Table, Modal, Select, Button, message } from "antd";
import { UserOutlined, MailOutlined } from "@ant-design/icons";
import useSupportService from "../../../../services/supportService";
import moment from "moment"; // Für Datumsformatierung
const { Option } = Select;

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { getAllTickets, updateTicket } = useSupportService();
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  useEffect(() => {
    const getTickets = async () => {
      try {
        const data = await getAllTickets();
        console.log("Fetched Tickets:", data);

        const formattedTickets = data.map((ticket) => ({
          key: ticket._id,
          _id: ticket._id,
          user: ticket.user?.name || "Unbekannt",
          email: ticket.user?.email || "Keine E-Mail verfügbar",
          subject: ticket.subject,
          description: ticket.decription,
          status: ticket.status || "Offen",
          priority: ticket.priority || "Niedrig",
          createdAt: moment(ticket.createdAt).format("DD.MM.YYYY HH:mm"),
        }));

        setTickets(formattedTickets);
      } catch (error) {
        console.error("Fehler beim Abrufen der Tickets:", error);
        message.error("Fehler beim Abrufen der Support-Tickets.");
      }
    };

    getTickets();
  }, []);

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setStatus(ticket.status);
    setPriority(ticket.priority);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedTicket(null);
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;

    try {
      await updateTicket(selectedTicket._id, {
        status,
        priority,
      });

      setTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket._id === selectedTicket._id
            ? { ...ticket, status, priority }
            : ticket
        )
      );

      handleCloseModal();
    } catch (error) {
      console.error("Fehler beim Aktualisieren:", error);
      message.error("Fehler beim Aktualisieren des Tickets.");
    }
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Offen", value: "Offen" },
        { text: "In Bearbeitung", value: "In Bearbeitung" },
        { text: "Gechlossen", value: "Geschlosen" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            status === "Offen"
              ? "bg-green-200 text-green-800"
              : status === "In Bearbeitung"
              ? "bg-yellow-200 text-yellow-800"
              : "bg-red-200 text-red-800"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      filters: [
        { text: "Niedrig", value: "Niedrig" },
        { text: "Mittel", value: "Mittel" },
        { text: "Hoch", value: "Hoch" },
      ],
      onFilter: (value, record) => record.priority === value,
      render: (priority) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            priority === "Hoch"
              ? "bg-red-200 text-red-800"
              : priority === "Mittel"
              ? "bg-yellow-200 text-yellow-800"
              : "bg-blue-200 text-blue-800"
          }`}
        >
          {priority}
        </span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewTicket(record)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Support Tickets</h1>
      <div className="overflow-x-auto custom-scrollbar">
        <Table columns={columns} dataSource={tickets} rowKey="_id" />
      </div>

      <Modal
        title="Ticket Details"
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Abbrechen
          </Button>,
          <Button
            key="save"
            type="primary"
            className="!bg-orange-500"
            onClick={handleUpdateTicket}
          >
            Änderungen speichern
          </Button>,
        ]}
      >
        {selectedTicket && (
          <div className="space-y-4">
            <div>
              <p className="font-semibold">User:</p>
              <p className="flex items-center">
                <UserOutlined className="mr-2" /> {selectedTicket.user}
              </p>
            </div>
            <div>
              <p className="font-semibold">Email:</p>
              <p className="flex items-center">
                <MailOutlined className="mr-2" /> {selectedTicket.email}
              </p>
            </div>
            <div>
              <p className="font-semibold">Titel:</p>
              <p>{selectedTicket.subject}</p>
            </div>
            <div>
              <p className="font-semibold">Beschreibung:</p>
              <p>{selectedTicket.decription}</p>
            </div>
            <div>
              <p className="font-semibold">Status:</p>
              <Select value={status} onChange={setStatus} className="w-full">
                <Option value="Offen">Offen</Option>
                <Option value="In Progress">In Bearbeitung</Option>
                <Option value="Closed">Geschlossen</Option>
              </Select>
            </div>
            <div>
              <p className="font-semibold">Priorität:</p>
              <Select
                value={priority}
                onChange={setPriority}
                className="w-full"
              >
                <Option value="Low">Niedrig</Option>
                <Option value="Medium">Mittel</Option>
                <Option value="High">Hoch</Option>
              </Select>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SupportTickets;
